import { MessageCircle, UploadCloud } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, CASES, toArabicDigits } from "@/lib/constants";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const Gallery = () => {
  const { t, isRTL } = useLanguage();
  return (
    <section id="gallery" className="relative py-20 md:py-28 bg-[#0c0c0c]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] tracking-[0.25em] uppercase font-display text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e10600]" />
              {t.gallery.eyebrow}
            </div>
            <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight">
              {t.gallery.title}
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-white/50 tracking-[0.15em] uppercase font-display">
            <UploadCloud className="w-3.5 h-3.5 text-[#e10600]" />
            {CASES.length} {t.gallery.casesLabel}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {CASES.map((c) => (
            <div
              key={c.id}
              className="group relative rounded-2xl border border-white/10 bg-[#111] p-4 md:p-5 hover:border-[#e10600]/40 transition"
              data-testid={`case-card-${c.id}`}
            >
              <div className="flex items-center justify-between mb-4 px-2">
                <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-display text-white/75">
                  <span className="w-6 h-6 rounded-full bg-[#e10600] text-white text-[11px] font-black flex items-center justify-center">
                    {isRTL ? toArabicDigits(c.id) : c.id}
                  </span>
                  {t.gallery.case} {isRTL ? toArabicDigits(c.id) : c.id}
                </span>
                <span className="text-[10px] tracking-[0.3em] text-white/40 uppercase font-display">
                  PDR
                </span>
              </div>
              <BeforeAfterSlider
                beforeSrc={c.before}
                afterSrc={c.after}
                caseNumber={c.id}
              />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 md:mt-16 rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a0606] via-[#111] to-[#0c0c0c] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#e10600]/15 blur-3xl pointer-events-none" />
          <p className="relative font-display font-bold text-xl md:text-2xl text-white max-w-2xl">
            {t.gallery.ctaLine}
          </p>
          <a
            href={BRAND.whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="relative inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#e10600] hover:bg-[#c20500] text-white font-bold transition shadow-xl shadow-red-900/40 shrink-0"
            data-testid="gallery-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            {t.gallery.cta}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
